import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, SearchIcon, MapPinIcon, DatabaseIcon, ShieldCheckIcon, UserIcon, SendIcon, ArrowRightIcon } from 'lucide-react';
const featurePages = [{
  name: 'Local business API',
  description: 'Search 200M+ businesses via API',
  href: '#/local-business-api',
  icon: SearchIcon
}, {
  name: 'Google Maps scraper',
  description: 'Extract business data from Google Maps',
  href: '#/google-maps-scraper',
  icon: MapPinIcon
}, {
  name: 'Local business database',
  description: 'Access verified local business listings',
  href: '#/local-business-database',
  icon: DatabaseIcon
}, {
  name: 'Local business enrichment',
  description: 'Enrich your existing business data',
  href: '#/local-business-enrichment',
  icon: ShieldCheckIcon
}, {
  name: 'Owner finder',
  description: 'Find business owner contact info',
  href: '#/owner-finder',
  icon: UserIcon
}, {
  name: 'Local marketing',
  description: 'Target local businesses with campaigns',
  href: '#/local-marketing',
  icon: MapPinIcon
}, {
  name: 'Email sequencing',
  description: 'Built-in multi-step email outreach',
  href: '#/email-sequencing',
  icon: SendIcon
}];
const databaseCategories = {
  Healthcare: [{
    name: 'Doctors',
    href: 'https://www.openmart.com/data/doctors'
  }, {
    name: 'Dentists',
    href: 'https://www.openmart.com/data/dentists'
  }, {
    name: 'Chiropractic',
    href: 'https://www.openmart.com/data/chiropractic'
  }, {
    name: 'Veterinary',
    href: 'https://www.openmart.com/data/veterinary-clinics'
  }, {
    name: 'Optometry',
    href: 'https://www.openmart.com/data/optometry'
  }],
  'Home Services': [{
    name: 'Plumbing',
    href: 'https://www.openmart.com/data/plumbing'
  }, {
    name: 'HVAC',
    href: 'https://www.openmart.com/data/hvac'
  }, {
    name: 'Electricians',
    href: 'https://www.openmart.com/data/electricians'
  }, {
    name: 'Landscaping',
    href: 'https://www.openmart.com/data/landscaping'
  }, {
    name: 'Roofing',
    href: 'https://www.openmart.com/data/roofing'
  }],
  'Food & Retail': [{
    name: 'Restaurants',
    href: 'https://www.openmart.com/data/restaurants'
  }, {
    name: 'Bakeries',
    href: 'https://www.openmart.com/data/bakeries'
  }, {
    name: 'Car Dealers',
    href: 'https://www.openmart.com/data/car-dealer'
  }, {
    name: 'Pet Stores',
    href: 'https://www.openmart.com/data/pet-stores'
  }, {
    name: 'Grocery',
    href: 'https://www.openmart.com/data/grocery'
  }],
  'Professional Services': [{
    name: 'Accounting',
    href: '#/data/accounting'
  }, {
    name: 'Law Firms',
    href: 'https://www.openmart.com/data/law-firms'
  }, {
    name: 'Insurance',
    href: 'https://www.openmart.com/data/insurance-agencies'
  }, {
    name: 'Real Estate',
    href: 'https://www.openmart.com/data/real-estate'
  }, {
    name: 'Marketing Agencies',
    href: 'https://www.openmart.com/data/marketing-agencies'
  }, {
    name: 'Property Management',
    href: 'https://www.openmart.com/data/property-management'
  }]
};
export function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dbDropdownOpen, setDbDropdownOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dbTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handleFeaturesEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setDbDropdownOpen(false);
    setDropdownOpen(true);
  };
  const handleFeaturesLeave = () => {
    timeoutRef.current = setTimeout(() => setDropdownOpen(false), 150);
  };
  const handleDbEnter = () => {
    if (dbTimeoutRef.current) clearTimeout(dbTimeoutRef.current);
    setDropdownOpen(false);
    setDbDropdownOpen(true);
  };
  const handleDbLeave = () => {
    dbTimeoutRef.current = setTimeout(() => setDbDropdownOpen(false), 150);
  };
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (dbTimeoutRef.current) clearTimeout(dbTimeoutRef.current);
    };
  }, []);
  return <nav className="w-full bg-white py-4 relative z-50 border-b border-slate-100" data-id="element-3563">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3564">
        <div className="flex justify-between items-center h-12" data-id="element-3565">
          {/* Logo */}
          <a href="#/" className="flex-shrink-0 flex items-center gap-2 cursor-pointer" data-id="element-3566">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center" data-id="element-3567">
              <div className="w-3 h-3 border-2 border-white rounded-full border-t-transparent transform rotate-45" data-id="element-3568"></div>
            </div>
            <span className="font-bold text-2xl tracking-tight text-slate-900" data-id="element-3569">
              openmart
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8" data-id="element-3570">
            {/* Features Dropdown */}
            <div className="relative h-full flex items-center" onMouseEnter={handleFeaturesEnter} onMouseLeave={handleFeaturesLeave} data-id="element-3571">
              <button className={`flex items-center gap-1 font-medium transition-colors py-2 ${dropdownOpen ? 'text-blue-600' : 'text-slate-600 hover:text-slate-900'}`} onClick={() => {
              setDropdownOpen(!dropdownOpen);
              setDbDropdownOpen(false);
            }} data-id="element-3572">
                Features
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} data-id="element-3573" />
              </button>

              <AnimatePresence data-id="element-3574">
                {dropdownOpen && <motion.div initial={{
                opacity: 0,
                y: -8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -8
              }} transition={{
                duration: 0.15,
                ease: 'easeOut'
              }} className="absolute top-full left-0 pt-4 w-[380px]" data-id="element-3575">
                    <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden flex flex-col" data-id="element-3576">
                      <div className="p-3 space-y-1" data-id="element-3577">
                        {featurePages.map(page => <a key={page.href} href={page.href} className="flex items-start gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors group" onClick={() => setDropdownOpen(false)} data-id="element-3578">
                            <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center text-slate-500 group-hover:bg-white group-hover:text-blue-600 group-hover:shadow-sm transition-all flex-shrink-0" data-id="element-3579">
                              <page.icon className="w-5 h-5" data-id="element-3580" />
                            </div>
                            <div data-id="element-3581">
                              <div className="text-sm font-bold text-slate-900 mb-0.5 group-hover:text-blue-600 transition-colors" data-id="element-3582">
                                {page.name}
                              </div>
                              <div className="text-xs text-slate-500" data-id="element-3583">
                                {page.description}
                              </div>
                            </div>
                          </a>)}
                      </div>
                      <div className="bg-slate-50 p-4 border-t border-slate-100" data-id="element-3584">
                        <a href="#/features" className="flex items-center justify-between text-sm font-bold text-blue-600 hover:text-blue-700 transition-colors group" onClick={() => setDropdownOpen(false)} data-id="element-3585">
                          View all features
                          <ArrowRightIcon className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" data-id="element-3586" />
                        </a>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Databases Mega-Menu */}
            <div className="relative h-full flex items-center" onMouseEnter={handleDbEnter} onMouseLeave={handleDbLeave} data-id="element-3587">
              <button className={`flex items-center gap-1 font-medium transition-colors py-2 ${dbDropdownOpen ? 'text-blue-600' : 'text-slate-600 hover:text-slate-900'}`} onClick={() => {
              setDbDropdownOpen(!dbDropdownOpen);
              setDropdownOpen(false);
            }} data-id="element-3588">
                Databases
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${dbDropdownOpen ? 'rotate-180' : ''}`} data-id="element-3589" />
              </button>

              <AnimatePresence data-id="element-3590">
                {dbDropdownOpen && <motion.div initial={{
                opacity: 0,
                y: -8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -8
              }} transition={{
                duration: 0.15,
                ease: 'easeOut'
              }} className="absolute top-full left-1/2 -translate-x-1/2 pt-4 w-[800px]" data-id="element-3591">
                    <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden flex flex-col" data-id="element-3592">
                      {/* Top Highlight Row */}
                      <div className="bg-slate-50 p-6 border-b border-slate-100 flex items-center gap-6" data-id="element-3593">
                        <a href="#/data" className="flex-1 bg-white border border-slate-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-sm transition-all group" onClick={() => setDbDropdownOpen(false)} data-id="element-3594">
                          <div className="flex items-center gap-3 mb-1" data-id="element-3595">
                            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-3596">
                              <SearchIcon className="w-4 h-4" data-id="element-3597" />
                            </div>
                            <div className="text-sm font-bold text-slate-900 group-hover:text-blue-600 transition-colors" data-id="element-3598">
                              Browse all categories
                            </div>
                          </div>
                          <div className="text-xs text-slate-500 pl-11" data-id="element-3599">
                            Search our complete directory of 300+ industries
                          </div>
                        </a>
                        <a href="#/local-business-database" className="flex-1 bg-white border border-slate-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-sm transition-all group" onClick={() => setDbDropdownOpen(false)} data-id="element-3600">
                          <div className="flex items-center gap-3 mb-1" data-id="element-3601">
                            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-3602">
                              <DatabaseIcon className="w-4 h-4" data-id="element-3603" />
                            </div>
                            <div className="text-sm font-bold text-slate-900 group-hover:text-blue-600 transition-colors" data-id="element-3604">
                              Local business database
                            </div>
                          </div>
                          <div className="text-xs text-slate-500 pl-11" data-id="element-3605">
                            Access the full 200M+ local business database
                          </div>
                        </a>
                      </div>

                      {/* Columns */}
                      <div className="p-8 grid grid-cols-4 gap-8" data-id="element-3606">
                        {Object.entries(databaseCategories).map(([category, links]) => <div key={category} data-id="element-3607">
                              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-3608">
                                {category}
                              </h3>
                              <ul className="space-y-3" data-id="element-3609">
                                {links.map(link => <li key={link.name} data-id="element-3610">
                                    <a href={link.href} className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors block" onClick={() => setDbDropdownOpen(false)} data-id="element-3611">
                                      {link.name}
                                    </a>
                                  </li>)}
                              </ul>
                            </div>)}
                      </div>

                      {/* Bottom Footer */}
                      <div className="bg-slate-50 p-4 border-t border-slate-100 text-center" data-id="element-3612">
                        <a href="#/data" className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 hover:text-blue-700 transition-colors group" onClick={() => setDbDropdownOpen(false)} data-id="element-3613">
                          Browse all 300+ categories
                          <ArrowRightIcon className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" data-id="element-3614" />
                        </a>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <button className="text-slate-600 hover:text-slate-900 font-medium transition-colors py-2" data-id="element-3615">
              Pricing
            </button>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4" data-id="element-3616">
            <button className="px-5 py-2 text-sm font-bold text-slate-700 hover:text-blue-600 transition-colors" data-id="element-3617">
              Log in
            </button>
            <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="px-5 py-2.5 text-sm font-bold text-white bg-slate-900 rounded-full hover:bg-slate-800 transition-colors shadow-sm" data-id="element-3618">
              Try for free
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center" data-id="element-3619">
            <button className="text-slate-600 hover:text-slate-900 focus:outline-none p-2" data-id="element-3620">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-3621">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" data-id="element-3622" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </nav>;
}